import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconPlusLine,
  Modal,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ReviewService } from "../../../../services/ReviewService";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../DialogYesNo";
import { ReviewCycleContext } from "../Review";
import CourseReviewCycleEntry from "./CourseReviewCycleEntry";
import CycleCreation from "./CycleCreation";

const CourseReviewCyclesModal = ({ open, setOpen, cycle }) => {
  const showNotification = useNotification();
  const { courseReviewCycles, setCourseReviewCycles } =
    useContext(ReviewCycleContext);
  const cycleLengths = [
    { id: "1", label: t("courseReviewCycle.oneYear") },
    { id: "2", label: t("courseReviewCycle.twoYears") },
    { id: "5", label: t("courseReviewCycle.fiveYears") },
  ];
  const [upsertCycle, setUpsertCycle] = useState(!!cycle);
  const [editingCycle, setEditingCycle] = useState({
    id: cycle?.id || "",
    duration:
      cycleLengths.find((length) => length.label === cycle?.duration) || "",
    startYear: cycle?.start_date || {},
    courses: cycle?.reviewables || [],
    reflectionTemplateId: cycle?.reflection_template_id || "-1",
  });

  const [confirm, setConfirm] = useState(false);

  function verifyFields(duration, startYear, selectedCourses) {
    if (!duration || !startYear.id) {
      showNotification(t("courseReviewCycle.requiredFields"), ERROR);
      return false;
    } else if (selectedCourses.length === 0) {
      showNotification(t("courseReviewCycle.coursesRequired"), ERROR);
      return false;
    } else {
      return true;
    }
  }

  async function handleUpsertCycle(
    cycleId,
    duration,
    startYear,
    selectedCourses,
    reflectionTemplateId
  ) {
    try {
      const cyclePayload = { start_date: startYear, duration: duration.label };
      if (cycleId) {
        cyclePayload.id = cycleId;
      }
      const courseIds = selectedCourses.map((c) => c.id);

      const newCycle = await ReviewService.createOrUpdateReviewCycle(
        cyclePayload,
        courseIds,
        reflectionTemplateId
      );

      newCycle.reflection_template_id = reflectionTemplateId;
      newCycle.start_date = startYear;
      newCycle.reviewables = selectedCourses;
      if (cycleId) {
        setCourseReviewCycles((prevCycles) =>
          prevCycles.map((c) => (c.id === newCycle.id ? newCycle : c))
        );
        showNotification(
          t("notifications.general.updateSuccess", {
            obj: t("courseReviewCycle.courseReviewCycle"),
          })
        );
      } else {
        setCourseReviewCycles((prevCycles) => [...prevCycles, newCycle]);
        showNotification(
          t("notifications.general.createSuccess", {
            obj: t("courseReviewCycle.courseReviewCycle"),
          })
        );
      }
      if (cycle) {
        setOpen(false);
      } else {
        setUpsertCycle(false);
        setEditingCycle({
          id: "",
          duration: "",
          startYear: {},
          courses: [],
          reflectionTemplateId: "-1",
        });
      }
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("courseReviewCycle.courseReviewCycle"),
        }),
        ERROR
      );
    }
  }

  return (
    <Modal
      label={t("courseReviewCycle.manageCycles")}
      open={open}
      onClose={() => setOpen(false)}
      size="large"
      onExited={() => {
        setUpsertCycle(!!cycle);
        setEditingCycle({
          id: cycle?.id || "",
          duration:
            cycleLengths.find((length) => length.label === cycle?.duration) ||
            "",
          startYear: cycle?.start_date || {},
          courses: cycle?.reviewables || [],
          reflectionTemplateId: cycle?.reflection_template_id || "-1",
        });
      }}
    >
      <Modal.Header>
        <Heading level="h2" as="h1">
          {t("courseReviewCycle.manageCycles")}
        </Heading>
        <CloseButton
          placement="end"
          offset="medium"
          screenReaderLabel={t("global.close")}
          onClick={() => setOpen(false)}
        />
      </Modal.Header>
      <Modal.Body>
        {upsertCycle ? (
          <CycleCreation
            editingCycle={editingCycle}
            setEditingCycle={setEditingCycle}
            cycleLengths={cycleLengths}
          />
        ) : (
          <Flex wrap="wrap" gap="small">
            <Flex.Item shouldGrow textAlign="end">
              <Button
                color="primary"
                renderIcon={IconPlusLine}
                onClick={() => setUpsertCycle(true)}
              >
                {t("cyclesAndGoals.addButton")}
              </Button>
            </Flex.Item>
            {courseReviewCycles ? (
              courseReviewCycles.length > 0 ? (
                courseReviewCycles.map((cycle) => (
                  <Flex.Item key={cycle.id} size="100%">
                    <CourseReviewCycleEntry
                      cycle={cycle}
                      setUpsertCycle={setUpsertCycle}
                      setEditingCycle={setEditingCycle}
                      cycleLengths={cycleLengths}
                    />
                  </Flex.Item>
                ))
              ) : (
                <Flex.Item size="100%">
                  <Alert>{t("courseReviewCycle.noCyclesYet")}</Alert>
                </Flex.Item>
              )
            ) : (
              <>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
              </>
            )}
          </Flex>
        )}
      </Modal.Body>
      <Modal.Footer>
        {upsertCycle ? (
          <>
            <Button
              margin="0 small 0 0"
              onClick={() => {
                if (cycle) {
                  setOpen(false);
                } else {
                  setUpsertCycle(false);
                  setEditingCycle({
                    id: cycle?.id || "",
                    duration:
                      cycleLengths.find(
                        (length) => length.label === cycle?.duration
                      ) || "",
                    startYear: cycle?.start_date || {},
                    courses: cycle?.reviewables || [],
                    reflectionTemplateId: cycle?.reflection_template_id || "-1",
                  });
                }
              }}
            >
              {t("global.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={() => {
                if (
                  verifyFields(
                    editingCycle.duration,
                    editingCycle.startYear,
                    editingCycle.courses
                  )
                ) {
                  setConfirm(true);
                }
              }}
            >
              {editingCycle.id
                ? t("courseReviewCycle.updateCycle")
                : t("courseReviewCycle.createCycle")}
            </Button>
          </>
        ) : (
          <Button onClick={() => setOpen(false)}>{t("global.close")}</Button>
        )}
        <DialogYesNo
          message={t("courseReviewCycle.upsertConfirm")}
          openStatus={confirm}
          onClickNo={() => setConfirm(false)}
          onClickYes={async () => {
            await handleUpsertCycle(
              editingCycle.id,
              editingCycle.duration,
              editingCycle.startYear,
              editingCycle.courses,
              editingCycle.reflectionTemplateId
            );
            setConfirm(false);
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CourseReviewCyclesModal;
